import React, { useEffect, useMemo, useState } from 'react';

import { fetchStaffList } from '../../services/api/staffApi';
import { setActionPending } from '../../services/api/visitActionPendingApi';
import {
  DIRECTION_MATRIX,
  INTENTS_REQUIRING_COMMENT,
  INTENT_LABELS,
  ROLE_LABELS,
} from '../../constants/visitActionPending';
import { formatStaffName } from '../../utils/staffName';

/**
 * ActionPendingDialog — set a visit action-pending overlay.
 *
 * Caller-role-aware:
 *   - DOCTOR sees only Receptionist as a target role.
 *   - RECEPTIONIST sees Doctor / Nurse / Receptionist.
 *   - NURSE sees only Receptionist.
 *
 * Intent picker auto-narrows to the matrix-allowed intents for the
 * (callerRole, toRole) hop. Comment is required for COLLECT_PAYMENT
 * and ASSIGN_BACK_* intents.
 *
 * Phase 5 ships pure overlay (no procedure attach, no bundled payment).
 * Those side-effect inputs land in Phases 7 / 8 — the toggles render
 * as disabled placeholders here.
 *
 * Props:
 *   visit                — visit row (used for the "currently with"
 *                          display when an overlay already exists).
 *   viewer               — { memberId, role }
 *   defaultToRole        — optional pre-selection (e.g.
 *                          'RECEPTIONIST' from AttachPlanModal — Phase 7).
 *   defaultIntent        — optional pre-selection.
 *   onSubmitted          — (response) => void. Called after a
 *                          successful set.
 *   onCancel             — () => void
 */
export default function ActionPendingDialog({
  visit,
  viewer,
  defaultToRole = null,
  defaultIntent = null,
  onSubmitted,
  onCancel,
}) {
  const callerRole = (viewer?.role || '').toUpperCase();
  const allowedToRoles = Object.keys(DIRECTION_MATRIX[callerRole] || {});

  const [toRole, setToRole] = useState(
    defaultToRole && allowedToRoles.includes(defaultToRole)
      ? defaultToRole
      : (allowedToRoles[0] || ''),
  );

  const allowedIntents = (
    DIRECTION_MATRIX[callerRole]?.[toRole] || []
  );
  const [intent, setIntent] = useState(
    defaultIntent && allowedIntents.includes(defaultIntent)
      ? defaultIntent
      : (allowedIntents[0] || ''),
  );

  // When toRole changes, reset intent to first allowed for new pairing.
  useEffect(() => {
    if (allowedIntents.length === 0) {
      setIntent('');
      return;
    }
    if (!allowedIntents.includes(intent)) {
      setIntent(allowedIntents[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toRole]);

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStaffLoading(true);
    setStaffError(null);
    fetchStaffList()
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          setStaff(res.data || []);
        } else {
          setStaffError(res?.error || 'Failed to load staff');
        }
      })
      .finally(() => { if (!cancelled) setStaffLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const candidates = useMemo(() => {
    return (staff || [])
      .filter((m) => (m.role || '').toUpperCase() === toRole)
      .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
      .filter((m) => m.member_id !== viewer?.memberId);
  }, [staff, toRole, viewer]);

  const [toMemberId, setToMemberId] = useState('');
  useEffect(() => {
    setToMemberId('');
  }, [toRole]);

  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const commentRequired = INTENTS_REQUIRING_COMMENT.has(intent);
  const commentValid = !commentRequired || comment.trim().length > 0;
  const canSubmit = !!toRole && !!intent && !!toMemberId && commentValid;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const res = await setActionPending(visit.visit_id, {
        to_member_id: toMemberId,
        to_role: toRole,
        intent,
        comment: comment.trim() || null,
      });
      onSubmitted?.(res);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to set action');
    } finally {
      setBusy(false);
    }
  };

  if (allowedToRoles.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5">
          <h2 className="text-lg font-semibold mb-2">Send for action</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Your role ({ROLE_LABELS[callerRole] || callerRole || 'Unknown'})
            cannot send a visit for action.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-1">Send for action</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Park this visit with another member for a discrete task. The
          visit stays in your queue throughout.
        </p>

        {visit?.action_pending_with_member_id && (
          <div className="mb-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
            Currently with{' '}
            <strong>{visit.action_pending_with_name || visit.action_pending_with_member_id}</strong>
            {visit.action_pending_intent ? ` · ${INTENT_LABELS[visit.action_pending_intent] || visit.action_pending_intent}` : ''}.
            Setting a new action will replace it.
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Send to *</label>
          <select
            value={toRole}
            onChange={(e) => setToRole(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            required
          >
            {allowedToRoles.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">For *</label>
          <select
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            required
            disabled={!allowedIntents.length}
          >
            {allowedIntents.map((i) => (
              <option key={i} value={i}>{INTENT_LABELS[i] || i}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">
            {ROLE_LABELS[toRole] || 'Member'} *
          </label>
          {staffLoading ? (
            <div className="text-xs text-slate-500 dark:text-slate-400 py-2">
              Loading staff…
            </div>
          ) : staffError ? (
            <div className="text-xs text-rose-600 dark:text-rose-300 py-2">
              {staffError}
            </div>
          ) : (
            <select
              value={toMemberId}
              onChange={(e) => setToMemberId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              required
            >
              <option value="">— choose —</option>
              {candidates.map((m) => (
                <option key={m.member_id} value={m.member_id}>
                  {formatStaffName(m) || m.member_id}
                </option>
              ))}
            </select>
          )}
          {!staffLoading && !staffError && candidates.length === 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              No active {ROLE_LABELS[toRole] || toRole.toLowerCase()} members in this org.
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Comment {commentRequired ? '*' : '(optional)'}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            placeholder={
              commentRequired
                ? 'e.g. collect ₹500 — patient waiting'
                : 'Optional context for the receiver'
            }
            required={commentRequired}
          />
        </div>

        {errorMsg && (
          <div className="mb-3 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {errorMsg}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !canSubmit}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60"
          >
            {busy ? 'Sending…' : 'Send for action'}
          </button>
        </div>
      </form>
    </div>
  );
}
